import styled from "@emotion/styled";
import { Box, Flex } from "@otrium/core";
import { Heading, Text } from "@otrium/atoms";

const PromotionBannerWrapper = styled(Box)`
  position: relative;
  width: 100%;
  height: fit-content;
  cursor: pointer;
`;

const StyledBannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  margin: 0 auto;
`;

const StyledBannerHeading = styled(Heading)<{ textAlignment?: string }>`
  font-size: clamp(32px, 3vw + 16px, 48px);
  line-height: clamp(32px, 3vw + 16px, 48px);
  text-align: ${({ textAlignment }) => textAlignment || "center"};
  font-family: ${({ theme }) => theme.typography.fontFamily.PlusJakarta};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    text-align: center;
  }
`;

export const StyledBannerHeadingNew = styled(Heading)<{
  textAlignment?: string;
}>`
  text-align: ${({ textAlignment }) => textAlignment || "center"};
  font-size: clamp(32px, 3vw + 16px, 48px);
  line-height: clamp(32px, 3vw + 16px, 48px);
  margin: ${({ theme }) =>
    `-${theme.space.space8 || "8px"} ${theme.space.space0 || 0}`};
  max-height: calc(clamp(32px, 3vw + 16px, 48px) * 2);
  overflow: hidden;
  font-family: ${({ theme }) => theme.typography.fontFamily.PlusJakarta};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    text-align: center;
    margin: 0;
  }
`;

interface StyledBannerFakeButtonProps {
  textColor?: string;
  buttonColor?: string;
}

const StyledBannerFakeButton = styled.div<StyledBannerFakeButtonProps>`
  text-align: center;
  background-color: ${({ theme, buttonColor }) =>
    buttonColor || theme.colors.tone.white};
  color: ${({ theme, textColor }) => textColor || theme.colors.tone.black};
  padding: ${({ theme }) => `12px ${theme.space.space32}`};
  margin-bottom: ${({ theme }) => `${theme.space.space8}`};
  font-size: ${({ theme }) => `${theme.fontSizes.fontSize16}`};
  border-radius: 4px;
  max-width: calc(
    ${({ theme }) => `calc(${theme.fontSizes.fontSize48} * 4.5)`}
  );
  height: 48px;
  font-weight: 600;
  letter-spacing: 0.4px;
  line-height: 1.5;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints[1]}) {
    width: 100%;
    max-width: 100%;
  }
`;

const ContentWrapper = styled(Box)`
  width: 100%;
  height: 240px;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints[1]}) {
    padding: ${({ theme }) => `${theme.space.space16} ${theme.space.space24}`};
  }
`;

export const StyledContentWrapper = styled.div<{ textAlignment?: string }>`
  position: absolute;
  width: 100%;
  max-height: 100%;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: ${({ textAlignment }) => textAlignment || "center"};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    position: relative;
    justify-content: center;
  }
`;

const StyledBannerTypeTwoWrapper = styled(Flex)`
  flex-direction: row;
  @media (max-width: ${({ theme }) => theme.breakpoints[1]}) {
    flex-direction: column-reverse;
  }
`;

const StyledBannerTypeTwoImg = styled.img`
  width: 57%;

  @media (max-width: ${({ theme }) => theme.breakpoints[1]}) {
    width: 100%;
  }
`;

interface Props {
  backgroundColor: string;
}

const StyledBannerTypeTwoHeadingWrapper = styled(Box)<Props>`
  width: 43%;
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.space.space16} ${theme.space.space32}`};

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: -119px;
    width: 120px;
    height: 100%;
    background-color: ${({ backgroundColor }) => backgroundColor};
    clip-path: polygon(100% 0, 0 0, 0 100%);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints[1]}) {
    width: 100%;
    padding: ${({ theme }) =>
      `${theme.space.space16} ${theme.space.space16} ${theme.space.space24}`};
    &::after {
      content: unset;
    }
  }
`;

const StyledBannerTimerHeadingWrapper = styled(Box)<Props>`
  width: calc(50% - 32px);
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.space.space32}`};

  @media (max-width: ${({ theme }) => theme.breakpoints[1]}) {
    width: 100%;
    padding: ${({ theme }) =>
      `${theme.space.space16} ${theme.space.space16} ${theme.space.space24}`};
    margin-top: -2px;
  }
`;

const StyledBannerTimerHeading = styled(Heading)`
  font-family: ${({ theme }) => theme.typography.fontFamily.SSP};
`;

const StyledBannerTimerImg = styled.img`
  width: calc(50% + 32px);
  object-fit: cover;
  object-position: left center;

  @media (max-width: ${({ theme }) => theme.breakpoints[1]}) {
    width: 100%;
  }
`;

const StyledBannerTypeTwoHeading = styled(Heading)`
  font-family: ${({ theme }) => theme.typography.fontFamily.SSP};
`;

const StyledPromoCodeWrapper = styled.div<{
  promoCodeColor: string;
  outlineColor: string;
  textAlignment?: string;
}>`
  position: relative;
  display: flex;
  color: ${({ promoCodeColor }) => `${promoCodeColor}`};

  /*
  background-image instead of border (dashed) because it lies inside of the element
  it also allows setting the dashoffset
  */
  background-image: ${({ outlineColor }) =>
    `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='${encodeURIComponent(
      outlineColor
    )}FF' stroke-width='4' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`};

  border-radius: 4px;
  line-height: 24px;
  width: fit-content;
  max-height: 100%;
  justify-self: ${({ textAlignment }) => textAlignment || "center"};

  .icon {
    flex-shrink: 0;

    path {
      fill: ${({ theme, promoCodeColor }) =>
        promoCodeColor || theme.colors.tone.white};
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    justify-self: center;
  }
`;

const StyledPromoCodeText = styled.span`
  display: block;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
  margin-inline-start: ${({ theme }) => theme.space.space8};
`;

const StyledPromoCodePrompt = styled.span`
  display: flex;
  justify-content: left;
  align-items: center;
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  font-size: 0.9em;
  visibility: hidden;
  font-weight: 600;
  text-transform: uppercase;
  padding-inline-start: ${({ theme }) => theme.space.space64};
`;

const StyledPromoCode = styled.div<{ promoCodeColor: string }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: ${({ theme }) =>
    `${theme.space.space8 || "8px"} ${theme.space.space16 || "16px"} ${
      theme.space.space8 || "8px"
    } ${theme.space.space32 || "32px"}`};

  &:hover {
    > .prompt {
      visibility: visible;
    }

    > .code {
      visibility: hidden;
    }
  }
`;

const StyledPromoCodeWedge = styled.div<{ backgroundColor: string }>`
  position: relative;
  right: -1px;
  width: 20px;
  flex-shrink: 0;
  flex-grow: 0;
  flex-basis: 20px;
  position: relative;
  background-color: ${({ backgroundColor }) => backgroundColor};
  clip-path: polygon(100% 0, 0 100%, 100% 100%);
`;

const StyledPromoCodeButton = styled.div<{
  textColor: string;
  backgroundColor: string;
}>`
  position: relative;
  height: ${({ theme }) => theme.space.space48};
  padding: ${({ theme }) =>
    `12px ${theme.space.space32} 12px ${theme.space.space16}`};

  background-color: ${({ backgroundColor }) => `${backgroundColor}`};
  color: ${({ textColor }) => `${textColor}`};
  border-top-right-radius: ${({ theme }) => theme.space.space4};
  border-bottom-right-radius: ${({ theme }) => theme.space.space4};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
`;

export const StyledBannerContent = styled.div<{
  backgroundColor?: string;
  disableBackgroundColor?: boolean;
  textAlignment?: string;
}>`
  display: grid;
  grid-template-columns: minmax(0, calc( 480px - (42px * 2)));
  row-gap: ${({ theme }) => theme.space.space16};
  align-items: ${({ textAlignment }) => textAlignment || "center"};
  width: fit-content;
  height: auto;
  padding: ${({ theme }) => `${theme.space.space24 || "24px"}`};
  margin: ${({ theme }) => `${theme.space.space8 || "8px"}`};
  background-color: ${({ backgroundColor, disableBackgroundColor }) =>
    !disableBackgroundColor && backgroundColor
      ? backgroundColor
      : "transparent"};

  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    max-width: 100%;
    align-items: center;
    margin: 0;
    padding: ${({ theme }) =>
      `${theme.space.space16 || "16px"} ${theme.space.space24 || "24px"} ${
        theme.space.space24 || "24px"
      }`};
    background-color: ${({ backgroundColor }) => backgroundColor};
`;

export const StyledText = styled(Text)<{ textAlignment?: string }>`
  text-align: ${({ textAlignment }) => textAlignment || "center"};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    text-align: center;
  }
}
`;

export {
  PromotionBannerWrapper,
  StyledBannerImg,
  StyledBannerFakeButton,
  StyledBannerHeading,
  ContentWrapper,
  StyledBannerTypeTwoWrapper,
  StyledBannerTypeTwoHeading,
  StyledBannerTypeTwoImg,
  StyledBannerTypeTwoHeadingWrapper,
  StyledPromoCodeWrapper,
  StyledPromoCode,
  StyledPromoCodeButton,
  StyledPromoCodeWedge,
  StyledPromoCodeText,
  StyledPromoCodePrompt,
  StyledBannerTimerHeadingWrapper,
  StyledBannerTimerHeading,
  StyledBannerTimerImg,
};
